import { Fragment, type ReactNode } from 'react';
import { styled } from '@mui/material/styles';

import { BRAND } from '../../shared/theme/paletteDark';
import { FONTS } from '../../shared/theme/typography';
import { headingId, parseLegal } from './legalMarkdown';

/**
 * The Terms of Use and the Privacy Policy, rendered from their Markdown source.
 *
 * `components/MarkdownText` is deliberately not reused: it is an allow-list
 * parser for scraped event descriptions and has no headings and no tables, and
 * both of those carry meaning here — the Privacy Policy's four tables *are* the
 * disclosure, and flattening them into prose would change what the document
 * says. Four hundred lines of hand-written JSX would be worse again: nobody
 * would diff the next version of the document against it.
 *
 * Like `MarkdownText` this builds React elements rather than HTML. There is no
 * `dangerouslySetInnerHTML` anywhere in this app and this is not the file to
 * start one in.
 */

const Body = styled('div')({
  fontFamily: FONTS.body,
  fontSize: 16,
  lineHeight: 1.65,
  color: 'rgba(243,238,231,0.9)',

  '& h1': {
    margin: '0 0 6px',
    fontFamily: FONTS.serif,
    fontWeight: 700,
    fontSize: 'clamp(30px, 6vw, 42px)',
    lineHeight: 1.1,
    letterSpacing: '-0.01em',
    color: BRAND.parchment,
  },
  '& h2': {
    margin: '42px 0 10px',
    fontFamily: FONTS.display,
    fontWeight: 800,
    fontSize: 21,
    lineHeight: 1.25,
    color: BRAND.parchment,
    // Anchored cross-references land under the header rather than behind it.
    scrollMarginTop: 90,
  },
  '& p': { margin: '0 0 14px' },
  '& ul': { margin: '0 0 16px', paddingLeft: 22 },
  '& li': { margin: '0 0 8px' },
  '& li::marker': { color: 'rgba(243,238,231,0.45)' },
  '& strong': { fontWeight: 700, color: BRAND.parchment },
  '& a': {
    color: 'inherit',
    textDecoration: 'underline',
    textUnderlineOffset: 3,
    textDecorationColor: 'rgba(243,238,231,0.45)',
    overflowWrap: 'anywhere',
  },
  '& a:hover': { textDecorationColor: BRAND.parchment },
});

/**
 * A table on a phone: each row becomes its own block with the column headings
 * repeated in front of each cell, rather than a sideways scroll nobody finds.
 */
const Table = styled('table')(({ theme }) => ({
  width: '100%',
  margin: '0 0 22px',
  borderCollapse: 'collapse',
  fontSize: 14.5,
  lineHeight: 1.5,
  '& th, & td': {
    padding: '9px 12px',
    textAlign: 'left',
    verticalAlign: 'top',
    border: '1px solid rgba(243,238,231,0.18)',
  },
  '& th': {
    fontFamily: FONTS.display,
    fontWeight: 700,
    color: BRAND.parchment,
    background: 'rgba(243,238,231,0.06)',
  },
  [theme.breakpoints.down('sm')]: {
    '& thead': { position: 'absolute', width: 1, height: 1, overflow: 'hidden', clip: 'rect(0 0 0 0)' },
    '& tr': {
      display: 'block',
      margin: '0 0 12px',
      border: '1px solid rgba(243,238,231,0.18)',
      borderRadius: 10,
      overflow: 'hidden',
    },
    '& td': { display: 'block', border: 'none', borderTop: '1px solid rgba(243,238,231,0.12)' },
    '& td:first-of-type': { borderTop: 'none' },
    '& td::before': {
      content: 'attr(data-label)',
      display: 'block',
      marginBottom: 2,
      fontFamily: FONTS.display,
      fontWeight: 700,
      fontSize: 11,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: 'rgba(243,238,231,0.55)',
    },
  },
}));

const INLINE = /(\[[^\]]+\]\([^)\s]+\)|\*\*[^*]+\*\*|\*[^*\n]+\*)/g;
/** Only these ever become an anchor: our own routes, mail, and http(s). */
const SAFE_LINK = /^(https?:\/\/|mailto:|\/|#)/i;

function inline(text: string, key: string): ReactNode[] {
  return text.split(INLINE).map((part, i) => {
    const id = `${key}-${i}`;
    if (!part) return null;

    const link = /^\[([^\]]+)\]\(([^)\s]+)\)$/.exec(part);
    if (link) {
      const [, label, href] = link;
      if (!SAFE_LINK.test(href)) return <Fragment key={id}>{label}</Fragment>;
      const external = /^https?:/i.test(href);
      return (
        <a
          key={id}
          href={href}
          {...(external ? { target: '_blank', rel: 'noopener noreferrer' } : null)}
        >
          {label}
        </a>
      );
    }
    if (/^\*\*.+\*\*$/.test(part)) return <strong key={id}>{part.slice(2, -2)}</strong>;
    if (/^\*.+\*$/.test(part)) return <em key={id}>{part.slice(1, -1)}</em>;
    return <Fragment key={id}>{part}</Fragment>;
  });
}

export default function LegalDocument({ markdown }: { markdown: string }) {
  return (
    <Body>
      {parseLegal(markdown).map((block, i) => {
        if (block.kind === 'h') {
          const Tag = block.level === 1 ? 'h1' : 'h2';
          return (
            <Tag key={i} id={headingId(block.text)}>
              {inline(block.text, `h${i}`)}
            </Tag>
          );
        }
        if (block.kind === 'p') return <p key={i}>{inline(block.text, `p${i}`)}</p>;
        if (block.kind === 'ul') {
          return (
            <ul key={i}>
              {block.items.map((item, j) => (
                <li key={j}>{inline(item, `l${i}-${j}`)}</li>
              ))}
            </ul>
          );
        }
        return (
          <Table key={i}>
            <thead>
              <tr>
                {block.head.map((cell, j) => (
                  <th key={j} scope="col">
                    {inline(cell, `th${i}-${j}`)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {block.rows.map((row, j) => (
                <tr key={j}>
                  {row.map((cell, k) => (
                    <td key={k} data-label={block.head[k]}>
                      {inline(cell, `td${i}-${j}-${k}`)}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        );
      })}
    </Body>
  );
}
